import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  showingStart: number;
  showingEnd: number;
  totalCount: number;
  itemName?: string;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  loading = false,
  showingStart,
  showingEnd,
  totalCount,
  itemName = 'items'
}: PaginationProps) {
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate range around current page
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      // Adjust range if at the beginning or end
      if (currentPage <= 3) {
        startPage = 1;
        endPage = 5;
      } else if (currentPage >= totalPages - 2) {
        startPage = totalPages - 4;
        endPage = totalPages;
      }

      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }

      // Add pages in range
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis and last page if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (totalPages <= 1) {
    return null;
  }

  const pageNumbers = getPageNumbers();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      py: 3
    }}>
      {/* Count Display */}
      <Typography variant="body2" color="text.secondary" sx={{
        order: { xs: 2, sm: 1 },
        textAlign: { xs: 'center', sm: 'left' }
      }}>
        Showing {showingStart}-{showingEnd} of {totalCount.toLocaleString()} {itemName}
      </Typography>

      {/* Pagination Controls */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        order: { xs: 1, sm: 2 }
      }}>
        {loading && (
          <CircularProgress size={20} sx={{ mr: 2 }} />
        )}

        {/* Previous Button */}
        <IconButton
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1 || loading}
          size="small"
          sx={{
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'grey.100'
            },
            '&.Mui-disabled': {
              borderColor: 'grey.200',
              color: 'grey.400'
            }
          }}
        >
          <ChevronLeft fontSize="small" />
        </IconButton>

        {/* Page Numbers */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {pageNumbers.map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <Typography
                  variant="body2"
                  sx={{
                    px: 1,
                    py: 0.5,
                    color: 'text.secondary',
                    minWidth: 32,
                    textAlign: 'center'
                  }}
                >
                  ...
                </Typography>
              ) : (
                <Button
                  variant={currentPage === page ? 'contained' : 'outlined'}
                  color={currentPage === page ? 'primary' : 'inherit'}
                  size="small"
                  onClick={() => onPageChange(page as number)}
                  disabled={loading}
                  sx={{
                    minWidth: 32,
                    width: 32,
                    height: 32,
                    p: 0,
                    borderColor: 'grey.300',
                    color: currentPage === page ? 'primary.contrastText' : 'text.primary',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: currentPage === page ? 'primary.dark' : 'grey.100'
                    },
                    '&.Mui-disabled': {
                      borderColor: 'grey.200',
                      color: 'grey.400'
                    }
                  }}
                >
                  {page}
                </Button>
              )}
            </React.Fragment>
          ))}
        </Box>

        {/* Next Button */}
        <IconButton
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages || loading}
          size="small"
          sx={{
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'grey.100'
            },
            '&.Mui-disabled': {
              borderColor: 'grey.200',
              color: 'grey.400'
            }
          }}
        >
          <ChevronRight fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}